import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpParamsOptions, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';

import { environment } from '@env/environment';

import { map, tap } from 'rxjs/operators';

import { SortDirection } from '@angular/material/sort';

/**
 * DATA STRUCTURES
 */
export interface Artikel {
  id: number;
  nummer: string;
  beschreibung: string;
  beschreibung_2?: string;
  beschreibung_code?: string;
  art: string;
  basiseinheit: string;
  code: string;
  in_code?: string;
  alternative_beschreibung?: string;
  belegtext?: string;
  ausbringung?: string;
  sortierung?: number;
  zulassungen?: string;
  sicherheitsdatenblatt?: string;
  systemkomponente?: string;
  zulassungs_nummer?: string;
  wirkstoff?: string;
  status_id?: number;
  biozid_produktart?: string;
  usage: number;
}

export interface ArtikelStammdaten {
  artikel: Artikel[];
}

export interface Massnahme {
  id: number;
  beschreibung: string;
  druck_auf_bericht: number;
  foto_erforderlich: number;
  sortierung: number;
  status_id: number;
  indikator_id: number;
  freigeben: number;
  fertig_setzen: number;
}

export interface Abweichung {
  id: number;
  caption: string;
  description: string;
  sortierung: number;
  status: 'active' | 'inactive';
}

export interface MobileTextTemplate {
  id?: number;
  schluesselwort: string;
  text: string;
  sortierung?: number;
  status_id?: number;
}

@Injectable({
  providedIn: 'root'
})
export class StammdatenService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get artikel stammdaten
   */
  getArtikelStammdaten(): Observable<ArtikelStammdaten> {
    return this.http.get<{result: number, data: ArtikelStammdaten}>(`${this.apiUrl}/artikel-stammdaten`)
      .pipe(
        map(response => response.data || { artikel: [] }),
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Sync artikel stammdaten
   */
  syncArtikelStammdaten(stammdaten: ArtikelStammdaten): Observable<any> {
    return this.http.post(`${this.apiUrl}/artikel-stammdaten`, stammdaten)
      .pipe(
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Get all artikels (actual artikel records)
   */
  getArtikels(): Observable<Artikel[]> {
    return this.http.get<{data: Artikel[]}>(`${this.apiUrl}/artikel?limit=200`)
      .pipe(
        map(response => response.data || []),
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Get single artikel by id
   */
  getArtikel(id: number): Observable<Artikel> {
    return this.http.get<Artikel>(`${this.apiUrl}/artikel/${id}`)
      .pipe(
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Create new artikel
   */
  createArtikel(artikel: Partial<Artikel>): Observable<Artikel> {
    return this.http.post<{result: number, data: Artikel}>(`${this.apiUrl}/artikel`, artikel)
      .pipe(
        map(response => response.data || response as any),
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Update artikel
   */
  updateArtikel(id: number, artikel: Partial<Artikel>): Observable<Artikel> {
    return this.http.put<{result: number, data: Artikel}>(`${this.apiUrl}/artikel/${id}`, artikel)
      .pipe(
        map(response => response.data || response as any),
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Delete artikel
   */
  deleteArtikel(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/artikel/${id}`)
      .pipe(
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Get all massnahmen
   */
  getMassnahmen(): Observable<Massnahme[]> {
    return this.http.get<{data: Massnahme[]}>(`${this.apiUrl}/massnahme?limit=200`)
      .pipe(
        map(response => response.data || []),
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Get single massnahme
   */
  getMassnahme(id: number): Observable<Massnahme> {
    return this.http.get<Massnahme>(`${this.apiUrl}/massnahme/${id}`)
      .pipe(
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Create massnahme
   */
  createMassnahme(massnahme: Massnahme): Observable<Massnahme> {
    return this.http.post<{data: Massnahme}>(`${this.apiUrl}/massnahme`, massnahme)
      .pipe(
        map(response => response.data || response as any),
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Update massnahme
   */
  updateMassnahme(id: number, massnahme: Massnahme): Observable<Massnahme> {
    return this.http.put<{data: Massnahme}>(`${this.apiUrl}/massnahme/${id}`, massnahme)
      .pipe(
        map(response => response.data || response as any),
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Delete massnahme
   */
  deleteMassnahme(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/massnahme/${id}`)
      .pipe(
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Get all mobile text templates (Textbausteine). Pass includeInactive=true to
   * also load deactivated ones (for management screens).
   */
  getMobileTextTemplates(includeInactive: boolean = false): Observable<MobileTextTemplate[]> {
    const suffix = includeInactive ? '&includeInactive=true' : '';
    return this.http.get<{data: MobileTextTemplate[]}>(`${this.apiUrl}/mobile-text-template?limit=200${suffix}`)
      .pipe(
        map(response => response.data || []),
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Get single mobile text template
   */
  getMobileTextTemplate(id: number): Observable<MobileTextTemplate> {
    return this.http.get<{data: MobileTextTemplate}>(`${this.apiUrl}/mobile-text-template/${id}`)
      .pipe(
        map(response => response.data || response as any),
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Create mobile text template
   */
  createMobileTextTemplate(template: MobileTextTemplate): Observable<MobileTextTemplate> {
    return this.http.post<{data: MobileTextTemplate}>(`${this.apiUrl}/mobile-text-template`, template)
      .pipe(
        map(response => response.data || response as any),
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Update mobile text template
   */
  updateMobileTextTemplate(id: number, template: MobileTextTemplate): Observable<MobileTextTemplate> {
    return this.http.put<{data: MobileTextTemplate}>(`${this.apiUrl}/mobile-text-template/${id}`, template)
      .pipe(
        map(response => response.data || response as any),
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Delete (deactivate) mobile text template
   */
  deleteMobileTextTemplate(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/mobile-text-template/${id}`)
      .pipe(
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Get all abweichungen
   */
  getAbweichungen(): Observable<Abweichung[]> {
    return this.http.get<{data: Abweichung[]}>(`${this.apiUrl}/abweichung`)
      .pipe(
        map(response => response.data || []),
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Get single abweichung
   */
  getAbweichung(id: number): Observable<Abweichung> {
    return this.http.get<Abweichung>(`${this.apiUrl}/abweichung/${id}`)
      .pipe(
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Create abweichung
   */
  createAbweichung(abweichung: Abweichung): Observable<Abweichung> {
    return this.http.post<Abweichung>(`${this.apiUrl}/abweichung`, abweichung)
      .pipe(
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Update abweichung
   */
  updateAbweichung(id: number, abweichung: Abweichung): Observable<Abweichung> {
    return this.http.put<Abweichung>(`${this.apiUrl}/abweichung/${id}`, abweichung)
      .pipe(
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Delete abweichung
   */
  deleteAbweichung(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/abweichung/${id}`)
      .pipe(
        retry(3),
        catchError(this.handleError)
      );
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      console.error('An error occurred:', error.error.message);
    } else {
      // Server-side error
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`
      );
    }
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }
}